import { useCallback, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '@/components/v2/ui';
import { analyzeImageOptimize } from '@/lib/api/image-optimize';
import { type ImageOptimizeState } from './useImageOptimizeState';
import { AnalysisResults } from './AnalysisResults';

interface Props {
  state: ImageOptimizeState;
  setState: Dispatch<SetStateAction<ImageOptimizeState>>;
}

export function AnalysisGroup({ state, setState }: Props) {
  const { t, i18n } = useTranslation();

  const handleAnalyze = useCallback(async () => {
    if (!state.currentFile) return;
    setState(prev => ({ ...prev, isAnalyzing: true }));
    try {
      const result = await analyzeImageOptimize(state.currentFile, { language: i18n.language });
      setState(prev => ({ ...prev, analysisResult: result, isAnalyzing: false }));
    } catch {
      setState(prev => ({ ...prev, isAnalyzing: false }));
    }
  }, [state.currentFile, setState, i18n]);

  if (!state.currentFile) {
    return <p className="text-sm text-neutral-500">{t('imageOptimize.analysis.noImage')}</p>;
  }

  if (state.isAnalyzing) {
    return (
      <div className="flex items-center gap-2 py-4">
        <Spinner /> <span className="text-sm">{t('imageOptimize.analysis.analyzing')}</span>
      </div>
    );
  }

  if (!state.analysisResult) {
    return (
      <Button variant="primary" className="w-full" onClick={handleAnalyze}>
        {t('imageOptimize.analysis.analyze')}
      </Button>
    );
  }

  return <AnalysisResults state={state} onReanalyze={handleAnalyze} />;
}
